import { useCallback, useEffect, useState } from "react";
import { Send, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errText } from "@/lib/api";
import { ROLE_NAMES, formatDate } from "@/lib/constants";
import { hasProfanity, PROFANITY_WARNING } from "@/lib/profanity";

export function TicketThread({ ticketId, staff, onChange }) {
  const [t, setT] = useState(null);
  const [text, setText] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/support/${ticketId}`); setT(data); }
    catch (e) { toast.error(errText(e)); }
  }, [ticketId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 6000);
    return () => clearInterval(iv);
  }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (hasProfanity(text)) { toast.warning(PROFANITY_WARNING); return; }
    try {
      const { data } = await api.post(`/support/${ticketId}/messages`, { text });
      setT(data); setText(""); onChange?.();
    } catch (err) { toast.error(errText(err)); }
  };

  const toggle = async () => {
    try {
      const { data } = await api.patch(`/admin/support/${ticketId}`, { status: t.status === "open" ? "closed" : "open" });
      setT(data); onChange?.();
    } catch (err) { toast.error(errText(err)); }
  };

  if (!t) return <div className="p-8 text-sm text-[#4b6b5c]">Загрузка...</div>;
  return (
    <div className="flex flex-col h-full" data-testid="ticket-thread">
      <div className="flex items-start justify-between gap-3 p-5 border-b border-[#d1e7dd]">
        <div>
          <h3 className="font-display font-bold text-lg text-[#0b2e20]" data-testid="ticket-subject">{t.subject}</h3>
          <p className="text-xs text-[#6b8a7b]">от {t.author} · {formatDate(t.created_at)} · {t.status === "open" ? "Открыто" : "Закрыто"}</p>
        </div>
        {staff && (
          <Button variant="outline" size="sm" onClick={toggle} className="rounded-full" data-testid="ticket-toggle-status-button">
            {t.status === "open" ? <><Lock className="h-3.5 w-3.5 mr-1" />Закрыть</> : <><Unlock className="h-3.5 w-3.5 mr-1" />Открыть</>}
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-3 scrollbar-thin max-h-[420px]">
        {t.messages.map((m, i) => {
          const fromStaff = m.role !== "user";
          return (
            <div key={i} className={`max-w-[85%] rounded-2xl px-4 py-3 ${fromStaff ? "bg-[#1f5f42] text-white ml-auto" : "bg-[#e8f5ed] text-[#0b2e20]"}`} data-testid={`ticket-message-${i}`}>
              <div className={`text-xs font-bold mb-1 ${fromStaff ? "text-[#c8f2d6]" : "text-[#2e7d56]"}`}>
                {fromStaff ? `Поддержка · ${m.author} (${ROLE_NAMES[m.role]})` : m.author} · {formatDate(m.created_at)}
              </div>
              <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
            </div>
          );
        })}
      </div>
      {t.status === "open" ? (
        <form onSubmit={send} className="p-4 border-t border-[#d1e7dd] flex gap-2">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ваше сообщение..." className="resize-none" data-testid="ticket-reply-input" />
          <Button type="submit" className="rounded-full bg-[#1f5f42] hover:bg-[#174731] self-end h-11 w-11 p-0" data-testid="ticket-reply-send-button"><Send className="h-4 w-4" /></Button>
        </form>
      ) : (
        <div className="p-4 border-t border-[#d1e7dd] text-sm text-[#6b8a7b] text-center" data-testid="ticket-closed-note">Обращение закрыто</div>
      )}
    </div>
  );
}
